import { Avatar, ListItem } from "@design";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import type { SpaceActivityResponse } from "@/api";
import { Text } from "@/components/Text";
import { useCurrentUser } from "@/features/account/useCurrentUser";
import { formatRelativeTime } from "@/i18n/format";

const initials = (name?: string | null) =>
  (name ?? "?")
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? "")
    .join("");

const metaString = (item: SpaceActivityResponse, key: string): string | undefined => {
  const value = item.metadata?.[key];
  return typeof value === "string" ? value : undefined;
};

/**
 * Space activity rows, worded from the activity type (backend titles are a fallback).
 * A row opens its chat only when it's my own (other people's chats aren't shared with me);
 * in the cross-space feed (`showSpace`) other rows open their space instead.
 */
export function ActivityList({
  items,
  showSpace = false,
}: Readonly<{ items: SpaceActivityResponse[]; showSpace?: boolean }>) {
  const { t } = useTranslation("spaces", { keyPrefix: "detail.activity" });
  const me = useCurrentUser();
  const myEmail = me.data?.user.email;
  const navigate = useNavigate();

  const target = (item: SpaceActivityResponse): string | undefined => {
    const chat = metaString(item, "chat_session_uuid");
    if (chat && myEmail && item.userEmail === myEmail) return `/chat/${chat}`;
    if (showSpace && item.spaceUuid) return `/spaces/${item.spaceUuid}`;
    return undefined;
  };

  return (
    <ul>
      {items.map((item) => {
        const to = target(item);
        return (
          <li key={item.uuid}>
            <ListItem
              flat
              onClick={to ? () => navigate(to) : undefined}
              leading={<Avatar initials={initials(item.userName)} size="sm" />}
              description={showSpace ? (item.spaceName ?? undefined) : undefined}
              trailing={
                <Text as="span" variant="caption" tone="tertiary" className="shrink-0">
                  {formatRelativeTime(item.createdAt)}
                </Text>
              }
            >
              {t(`types.${item.activityType}` as "types.chat_started", {
                name: item.userName ?? t("someone"),
                filename: metaString(item, "filename") ?? t("aDocument"),
                defaultValue: item.title,
              })}
            </ListItem>
          </li>
        );
      })}
    </ul>
  );
}
